import { useWindowSize } from '../../../core/hooks/useWindowSize';
import { useAppState } from '../../../core/redux/hooks';
import { isRtl } from '../../../localization/module';
import { useMsgText } from '../../../localization/hooks';
import { Str } from '../../../localization/translationKeys';
import { useMemo, useState, useCallback, useEffect } from 'react';
import { colorForStatus } from './module';
import { useLoading } from '../../../core/api/hooks';
import { ActionType } from '../../../core/redux/actionType';
import { isDotNetDefault, BENCHMARKS } from '../../../language/time';
import { updateDevPlanActionCompleteByDateAction } from './api/updateActionCompleteByAction';
import { useDispatch } from 'react-redux';
import { updateDevPlanActionStatusAction } from './api/updateDevPlanActionStatusAction';


export type CompletionDate = {
  selectedDate: Date,
  onChangeDate: (newDate: Date) => void,
  loading: boolean,
};


/**
 * Hook for Dev Plan's Item's Action's Completion Date read/write logic.
 */
export const useCompletionDate = (
  devPlanItemId: number,
  actionId: number,
  completeBy: string | Date,
): CompletionDate => {
  const dispatch = useDispatch();

  const [selectedDate, setSelectedDate] = useState<Date>(
    isDotNetDefault(completeBy) ? BENCHMARKS.Y2K : new Date(completeBy),
  );

  const loading = useLoading(ActionType.UpdateDevPlanActionCompleteByDateRequest, actionId);

  const onChangeDate = useCallback(
    newDate => {
      setSelectedDate(newDate);
      dispatch(updateDevPlanActionCompleteByDateAction(devPlanItemId, actionId, newDate));
    },
    [actionId, devPlanItemId, setSelectedDate, dispatch],
  );

  return { selectedDate, onChangeDate, loading };
};


export type StatusOption = {
  statusId: number;
  txt: string;
};

type ActionStatus = {
  color: string;
  statuses: StatusOption[];
  selected: number;
  onChange: (event: any) => void;
  loading: boolean;
};

/**
 * Hook for Dev Plan's Item's Action's status read/write/render logic.
 */
export const useActionStatus = (
  itemId: number,
  actionId: number,
  status: number,
): ActionStatus => {
  const dispatch = useDispatch();

  const { isMobile } = useWindowSize();

  const translations = useAppState(state => state.locale.translations[state.locale.lang.languageId]);
  const rtl          = useAppState(state => isRtl(state.locale.textDirection));

  const title      = useMsgText(Str.Status);
  const statusMsgs = useAppState(state => state.devPlan?.actionStatuses || []);

  const [selected, setSelected] = useState<number>(status);
  const color                   = useMemo(() => colorForStatus(selected), [selected]);

  const loading = useLoading(ActionType.UpdateDevPlanActionStatusRequest, actionId);

  const projectId = useAppState(state => state.projectSummary?.projectId ?? 0);
  const devPlanProjectId = useAppState(state => state.projectSummary?.devPlanProjectId || projectId);

  const statuses = useMemo(
    () => {
      const getStr = (str: Str): string => {
        const translation = translations[str]?.text || '';

        // On mobile, stick the header on each of the drop-down options, honoring RTL reversal
        return isMobile
          ? rtl
            ? `${translation} :${title}`
            : `${title}: ${translation}`
          : translation;
      };

      return statusMsgs.map(msg => ({
        statusId: msg.statusId,
        txt:      getStr(msg.key),
      }));
    },
    [title, isMobile, rtl, statusMsgs, translations],
  );

  useEffect(
    () => {
      if (status !== selected) {
        dispatch(updateDevPlanActionStatusAction(devPlanProjectId, itemId, actionId, selected));
      }
    },
    [status, devPlanProjectId, itemId, actionId, selected, dispatch],
  );

  const onChange = useCallback(event => setSelected(+event.target.value), [setSelected]);

  return { color, statuses, selected, onChange, loading };
};
